import React, { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Per-product review widget: toggleable form + first 2 reviews + "see all" link.
 * Stateless — all state (reviews dict, reviewForm, showReviewForm) lives in the parent.
 */
export const ReviewSection = ({
  articleId,
  reviews,
  showReviewForm,
  reviewForm,
  setReviewForm,
  onToggleForm,
  onSubmit,
}) => {
  const productReviews = reviews[articleId] || [];
  const isFormOpen = !!showReviewForm[articleId];
  const [showAll, setShowAll] = useState(false);
  const avg = productReviews.length
    ? productReviews.reduce((s, r) => s + Number(r.rating || 0), 0) / productReviews.length
    : 0;

  return (
    <div className="pt-4 border-t border-gray-800 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-sm text-white">Recensioni</h4>
          {productReviews.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-300" data-testid={`review-summary-${articleId}`}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className={`w-3.5 h-3.5 ${star <= Math.round(avg) ? "text-yellow-400 fill-yellow-400" : "text-gray-600"}`} />
              ))}
              <span className="font-bold text-white">{avg.toFixed(1)}</span>
              <span className="text-gray-500">({productReviews.length})</span>
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onToggleForm(articleId)}
          className="text-xs bg-transparent border-gray-700 text-gray-300 hover:bg-pink-500/10 hover:text-pink-300 hover:border-pink-500/50"
          data-testid={`review-toggle-${articleId}`}
        >
          {isFormOpen ? "✕ Chiudi" : "⭐ Lascia Recensione"}
        </Button>
      </div>

      {isFormOpen && (
        <div className="space-y-3 p-3 bg-[#0d0d14] border border-gray-800 rounded-lg mb-3">
          <input
            type="text"
            placeholder="Il tuo nome"
            value={reviewForm.customer_name}
            onChange={(e) => setReviewForm((prev) => ({ ...prev, customer_name: e.target.value }))}
            className="w-full px-3 py-2 text-sm bg-[#12121a] border border-gray-700 rounded text-white placeholder-gray-500"
            data-testid={`review-name-${articleId}`}
          />
          <input
            type="email"
            placeholder="La tua email"
            value={reviewForm.customer_email}
            onChange={(e) => setReviewForm((prev) => ({ ...prev, customer_email: e.target.value }))}
            className="w-full px-3 py-2 text-sm bg-[#12121a] border border-gray-700 rounded text-white placeholder-gray-500"
            data-testid={`review-email-${articleId}`}
          />
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-300">Voto:</span>
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-5 h-5 cursor-pointer ${
                  star <= reviewForm.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-600"
                }`}
                onClick={() => setReviewForm((prev) => ({ ...prev, rating: star }))}
                data-testid={`review-star-${articleId}-${star}`}
              />
            ))}
          </div>
          <textarea
            placeholder="La tua recensione..."
            value={reviewForm.comment}
            onChange={(e) => setReviewForm((prev) => ({ ...prev, comment: e.target.value }))}
            className="w-full px-3 py-2 text-sm bg-[#12121a] border border-gray-700 rounded text-white placeholder-gray-500"
            rows={3}
            data-testid={`review-comment-${articleId}`}
          />
          <Button
            size="sm"
            onClick={() => onSubmit(articleId)}
            className="w-full bg-pink-600 hover:bg-pink-500 text-white font-bold"
            data-testid={`review-submit-${articleId}`}
          >
            Invia Recensione
          </Button>
        </div>
      )}

      {productReviews.length > 0 ? (
        <div className="space-y-2">
          {(showAll ? productReviews : productReviews.slice(0, 2)).map((review) => (
            <div key={review.id} className="p-2 bg-[#0d0d14] border border-gray-800 rounded text-xs" data-testid={`product-review-${review.id}`}>
              <div className="flex items-center space-x-1 mb-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-3 h-3 ${
                      star <= review.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-600"
                    }`}
                  />
                ))}
                <span className="font-semibold text-gray-200">{review.customer_name}</span>
                {review.verified_purchase && (
                  <span className="text-green-500 text-xs">✓ Verificato</span>
                )}
              </div>
              <p className="text-gray-400">{review.comment}</p>
            </div>
          ))}
          {productReviews.length > 2 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="text-xs text-pink-400 hover:text-pink-300 font-semibold"
              data-testid={`review-show-all-${articleId}`}
            >
              {showAll ? "Mostra meno" : `Vedi tutte le ${productReviews.length} recensioni`}
            </button>
          )}
        </div>
      ) : (
        <p className="text-xs text-gray-600 italic">Nessuna recensione ancora. Sii il primo!</p>
      )}
    </div>
  );
};
